const positiveId = (value) => {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

export const getFormatOfferId = (value) => positiveId(value?.formatOfferId)
  || (value?.promotionSnapshot?.kind === 'format_offer'
    ? positiveId(value.promotionSnapshot.formatOfferId) : null);
export const isFormatOfferPrice = (price) => Boolean(getFormatOfferId(price));
// Checkout persists quantity to three decimals. Multiplying the scaled integer
// first avoids a peso lost to binary rounding (200 * 1.275 is 254.999...).
export const computeFormatOfferSubtotal = (price, quantity) => {
  const amount = Number(price);
  const units = Number(quantity);
  if (!Number.isFinite(amount) || !Number.isFinite(units) || amount < 0 || units <= 0) return 0;
  return Math.max(Math.floor(amount * Math.round(units * 1000) / 1000), 0);
};
export const getSaleOptionId = (item) => {
  const offerId = getFormatOfferId(item);
  return offerId ? -offerId : Number(item?.sourceVarietyPriceId ?? item?.varietyPriceId ?? item?.id);
};
export const isFormatOfferSelectable = (price) => Boolean(price)
  && !price.formatOfferExpired && !price.formatOfferReplaced;

// Sellable overlays only: never mutate normal configuration. Stable slot order
// and retained expired options keep index-keyed drafts on their original price.
export const expandProductsWithFormatOffers = (products = [], previousProducts = []) => {
  const previousVarieties = new Map((previousProducts || []).flatMap((product) => (
    (product.Varieties || []).map((variety) => [String(variety.id), variety])
  )));
  return (products || []).map((product) => ({
    ...product,
    Varieties: (product.Varieties || []).map((variety) => {
      const normalPrices = (variety.VarietyPrices || []).filter((price) => !isFormatOfferPrice(price));
      const prices = normalPrices.flatMap((price) => {
        const offer = price.formatOffer;
        const offerId = positiveId(offer?.id);
        const offerPrice = Number(offer?.sellingPrice);
        const active = offerId && Number(offer.varietyPriceId) === Number(price.id)
          && offer?.sellingPrice != null && Number.isFinite(offerPrice) && offerPrice >= 0
          && ['alongside', 'replace'].includes(offer.mode);
        const normal = { ...price, formatOfferReplaced: Boolean(active && offer.mode === 'replace') };
        if (!active) return [normal];
        return [normal, {
          ...price,
          id: -offerId,
          parentVarietyPriceId: Number(price.id),
          formatOfferId: offerId,
          isFormatOffer: true,
          formatOfferExpired: false,
          formatOfferReplaced: false,
          price: offerPrice,
          finalPrice: offerPrice,
          stockPriceRule: null,
          discount: 0,
          discountSource: 'format_offer',
          discountType: null,
          discountValue: 0,
          description: [price.description, 'Oferta'].filter(Boolean).join(' · '),
        }];
      });
      const byId = new Map(prices.map((price) => [String(price.id), price]));
      const ordered = [];
      (previousVarieties.get(String(variety.id))?.VarietyPrices || []).forEach((previous) => {
        const current = byId.get(String(previous.id));
        if (current) {
          ordered.push(current);
          byId.delete(String(previous.id));
        } else if (isFormatOfferPrice(previous)) {
          ordered.push({ ...previous, formatOfferExpired: true, isSoldOut: true });
        }
      });
      return { ...variety, VarietyPrices: [...ordered, ...byId.values()] };
    }),
  }));
};

export const getFormatOfferLineIssue = (item, variety) => {
  const id = getSaleOptionId(item);
  const price = (variety?.VarietyPrices || []).find((entry) => Number(entry.id) === id);
  if (getFormatOfferId(item) && (!price || price.formatOfferExpired)) {
    return { title: 'Oferta finalizada', detail: 'Retira esta línea y selecciona un precio vigente. No se cambió su precio automáticamente.' };
  }
  if (price?.formatOfferReplaced) {
    return { title: 'Solo oferta disponible', detail: 'Retira el formato normal y selecciona la oferta vigente.' };
  }
  return null;
};

export const restoreProductsWithOfferItems = (products, items = []) => (products || []).map((product) => ({
  ...product,
  Varieties: (product.Varieties || []).map((variety) => {
    const prices = [...(variety.VarietyPrices || [])];
    (items || []).forEach((item) => {
      const offerId = getFormatOfferId(item);
      if (!offerId || Number(item.varietyId) !== Number(variety.id)
        || prices.some((price) => Number(price.id) === -offerId)) return;
      const parent = prices.find((price) => Number(price.id) === Number(item.varietyPriceId));
      prices.push({
        ...parent,
        id: -offerId,
        parentVarietyPriceId: Number(item.varietyPriceId),
        formatOfferId: offerId,
        promotionSnapshot: item.promotionSnapshot,
        isFormatOffer: true,
        formatOfferExpired: true,
        formatOfferReplaced: false,
        isSoldOut: true,
        price: Number(item.price || 0),
        metricUnit: item.metricUnit || parent?.metricUnit,
        description: 'Oferta finalizada',
      });
    });
    return { ...variety, VarietyPrices: prices };
  }),
}));

// Finalized documents render the monetary snapshot captured when they were
// finalized, even if a live catalog price changes later. Restore missing offer
// rows first so promotional and normal lines can both be resolved by ID.
export const restoreProductsWithItemPriceSnapshots = (products, items = []) => {
  const restoredProducts = restoreProductsWithOfferItems(products, items);
  const snapshots = new Map();
  (items || []).forEach((item) => {
    const varietyId = Number(item?.varietyId);
    const saleOptionId = getSaleOptionId(item);
    const rawPrice = item?.appliedBasePrice ?? item?.price;
    const price = Number(rawPrice);
    const snapshotKey = `${varietyId}:${saleOptionId}`;
    if (!Number.isInteger(varietyId) || !Number.isFinite(saleOptionId)
      || !Number.isFinite(price) || snapshots.has(snapshotKey)) return;
    const discount = Math.min(Math.max(Number(item?.discount || 0), 0), Math.max(price, 0));
    snapshots.set(snapshotKey, {
      price,
      finalPrice: Math.max(price - discount, 0),
    });
  });
  if (!snapshots.size) return restoredProducts;

  // A finalized quotation must remain renderable after catalog cleanup. The
  // quotation item is the durable display snapshot, so recreate only the
  // missing hierarchy needed to resolve that historical line. Existing live
  // products, varieties and formats keep their normal catalog metadata.
  const hydratedProducts = restoredProducts.map((product) => ({
    ...product,
    Varieties: (product.Varieties || []).map((variety) => ({ ...variety, VarietyPrices: [...(variety.VarietyPrices || [])] })),
  }));

  const varietyEntries = new Map();
  const productBySnapshotName = new Map();
  hydratedProducts.forEach((product) => {
    const productName = String(product?.productName || '').trim().toLocaleLowerCase();
    if (productName && !productBySnapshotName.has(productName)) {
      productBySnapshotName.set(productName, product);
    }
    (product.Varieties || []).forEach((variety) => {
      varietyEntries.set(Number(variety.id), { product, variety });
    });
  });

  (items || []).forEach((item) => {
    const varietyId = Number(item?.varietyId);
    const saleOptionId = getSaleOptionId(item);
    const rawPrice = item?.appliedBasePrice ?? item?.price;
    const price = Number(rawPrice);
    if (!Number.isInteger(varietyId) || !Number.isFinite(saleOptionId) || !Number.isFinite(price)) return;

    let entry = varietyEntries.get(varietyId);
    if (!entry) {
      const productName = String(item?.productName || '').trim();
      const productNameKey = productName.toLocaleLowerCase();
      let product = productNameKey ? productBySnapshotName.get(productNameKey) : null;
      if (!product) {
        product = {
          id: `historical-product-${varietyId}`,
          productName: productName || 'Producto histórico',
          productImage: item?.productImage || null,
          isHistoricalSnapshot: true,
          Varieties: [],
        };
        hydratedProducts.push(product);
        if (productNameKey) productBySnapshotName.set(productNameKey, product);
      }
      const variety = {
        id: varietyId,
        varietyName: String(item?.varietyName || '').trim() || 'Variedad histórica',
        varietyImage: item?.varietyImage || null,
        isHistoricalSnapshot: true,
        VarietyPrices: [],
      };
      product.Varieties.push(variety);
      entry = { product, variety };
      varietyEntries.set(varietyId, entry);
    }

    if ((entry.variety.VarietyPrices || []).some((candidate) => Number(candidate.id) === saleOptionId)) return;
    const formatOfferId = getFormatOfferId(item);
    const discount = Math.min(Math.max(Number(item?.discount || 0), 0), Math.max(price, 0));
    entry.variety.VarietyPrices.push({
      id: saleOptionId,
      ...(formatOfferId ? {
        parentVarietyPriceId: Number(item?.varietyPriceId),
        formatOfferId,
        isFormatOffer: true,
        formatOfferExpired: true,
      } : {}),
      price,
      finalPrice: Math.max(price - discount, 0),
      metricUnit: item?.metricUnit || null,
      clientType: item?.productFormatCode || item?.priceType || null,
      productFormatCode: item?.productFormatCode || item?.priceType || null,
      productFormatName: item?.productFormatName || null,
      description: item?.priceDescription || item?.description || 'Formato histórico',
      priceImage: item?.priceImage || null,
      promotionSnapshot: item?.promotionSnapshot || null,
      isHistoricalSnapshot: true,
    });
  });

  return hydratedProducts.map((product) => ({
    ...product,
    Varieties: (product.Varieties || []).map((variety) => ({
      ...variety,
      VarietyPrices: (variety.VarietyPrices || []).map((price) => {
        const snapshot = snapshots.get(`${Number(variety.id)}:${Number(price.id)}`);
        return snapshot ? { ...price, ...snapshot } : price;
      }),
    })),
  }));
};

export const getFormatOfferCartMessage = (products, quantities, scaleLines = []) => {
  for (const product of products || []) {
    for (const variety of product.Varieties || []) {
      for (const [index, price] of (variety.VarietyPrices || []).entries()) {
        const hasQuantity = Number(quantities?.[variety.id]?.[index] || 0) > 0
          || scaleLines.some((line) => Number(line.varietyId) === Number(variety.id)
            && (getSaleOptionId(line) === Number(price.id) || Number(line.priceIndex) === index)
            && Number(line.quantity || 0) > 0);
        if (hasQuantity && !isFormatOfferSelectable(price)) {
          return price.formatOfferExpired
            ? 'Una oferta del carrito finalizó. Retira esa línea y selecciona un precio vigente.'
            : 'Un formato del carrito ahora se vende solo en oferta. Retira la línea normal y selecciona la oferta.';
        }
      }
    }
  }
  return '';
};
